/**
 * Helpers de fecha/hora para PVP-Transporte.
 * Sin dependencias Angular — puro TypeScript. Usable en domain/ sin violar la regla de dependencia.
 */

import type { ISODateTime } from './branded-types';

/** Días hábiles argentinos (sin feriados dinámicos — los fijos más comunes). */
const FERIADOS_FIJOS: ReadonlySet<string> = new Set([
  '01-01', // Año Nuevo
  '05-01', // Día del Trabajador
  '05-25', // Revolución de Mayo
  '07-09', // Día de la Independencia
  '12-08', // Inmaculada Concepción
  '12-25', // Navidad
]);

function esFeriado(date: Date): boolean {
  const key = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return FERIADOS_FIJOS.has(key);
}

function esDiaHabil(date: Date): boolean {
  const dow = date.getDay(); // 0=Dom, 6=Sab
  return dow !== 0 && dow !== 6 && !esFeriado(date);
}

/**
 * Calcula la fecha límite sumando `horasHabiles` horas hábiles (lun–vie, sin feriados fijos).
 * Cada día hábil = 8h. Se usa para la regla de vencimiento de 72h hábiles (9 días hábiles).
 */
export function sumarHorasHabiles(desde: Date, horasHabiles: number): Date {
  const horasPorDia = 8;
  let diasHabiles = Math.floor(horasHabiles / horasPorDia);
  const horasRestantes = horasHabiles % horasPorDia;

  const result = new Date(desde);
  while (diasHabiles > 0) {
    result.setDate(result.getDate() + 1);
    if (esDiaHabil(result)) diasHabiles--;
  }
  result.setHours(result.getHours() + horasRestantes);
  return result;
}

/**
 * Verifica si `vencimiento` está dentro de las próximas `horasHabiles` horas hábiles.
 * Regla clave: alertar con ≥72h hábiles de anticipación.
 */
export function venceEnHorasHabiles(vencimiento: Date, horasHabiles: number, ahora = new Date()): boolean {
  const limite = sumarHorasHabiles(ahora, horasHabiles);
  return vencimiento <= limite && vencimiento >= ahora;
}

/** Retorna true si el documento ya venció. */
export function estaVencido(vencimiento: Date, ahora = new Date()): boolean {
  return vencimiento < ahora;
}

/** Formatea duración en ms como "Xh Ym". */
export function formatearDuracion(ms: number): string {
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Formato legible argentino: "dd/mm/aaaa HH:MM" */
export function formatearFecha(date: Date | ISODateTime | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Tiempo relativo legible en es-AR ("hace 5 minutos", "hace 2 horas", "recién").
 * Basado en `Intl.RelativeTimeFormat` (sin dependencias). Se usa en la bandeja de
 * notificaciones para la fecha relativa de cada ítem. `ahora` es inyectable para
 * tests deterministas.
 */
const RELATIVE_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

export function tiempoRelativo(date: Date | string, ahora: Date = new Date()): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const diffMs = d.getTime() - ahora.getTime();
  const absSec = Math.abs(diffMs) / 1000;
  if (absSec < 45) return 'recién';

  const rtf = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });
  for (const [unit, secs] of RELATIVE_UNITS) {
    if (absSec >= secs) {
      return rtf.format(Math.round(diffMs / 1000 / secs), unit);
    }
  }
  return rtf.format(Math.round(diffMs / 1000 / 60), 'minute');
}

/** Formato solo fecha: "dd/mm/aaaa" */
export function formatearSoloFecha(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Verifica si un horario cae en jornada nocturna (22:00–05:00). */
export function esHorarioNocturno(date: Date): boolean {
  const h = date.getHours();
  return h >= 22 || h < 5;
}

/** Parse seguro de ISO-8601 → Date | null */
export function parseISO(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Días entre dos fechas (absoluto). */
export function diasEntre(a: Date, b: Date): number {
  return Math.abs(Math.floor((b.getTime() - a.getTime()) / 86_400_000));
}
